/**
 * Active Pipelines Component
 * Displays all currently running pipelines with real-time progress
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  IconButton,
  Chip,
  Grid,
  Alert,
  Button,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StopIcon from '@mui/icons-material/Stop';
import { listActivePipelines, cancelPipeline } from '../services/datalakeGrpcService.ts';

interface ActivePipeline {
  pipelineId: string;
  creator: string;
  creatorId: string;
  purpose: string;
  modelName: string;
  status: string;
  progress: number;
  currentStep: number;
  startTime: string;
}

interface ActivePipelinesProps {
  onWatchPipeline?: (pipelineId: string) => void;
}

const ActivePipelines: React.FC<ActivePipelinesProps> = ({ onWatchPipeline }) => {
  const [pipelines, setPipelines] = useState<ActivePipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const loadPipelines = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listActivePipelines();
      setPipelines(response.pipelinesList || []);
    } catch (err: any) {
      console.error('Error loading active pipelines:', err);
      setError(err.message || 'Failed to load active pipelines');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPipeline = async (pipelineId: string) => {
    if (!window.confirm('Are you sure you want to cancel this pipeline?')) {
      return;
    }

    setCancellingId(pipelineId);
    try {
      const response = await cancelPipeline(pipelineId);
      if (response.success) {
        // Reload pipelines to remove cancelled one
        await loadPipelines();
      } else {
        alert(`Failed to cancel pipeline: ${response.message}`);
      }
    } catch (err: any) {
      console.error('Error cancelling pipeline:', err);
      alert(`Error cancelling pipeline: ${err.message}`);
    } finally {
      setCancellingId(null);
    }
  };

  const formatElapsedTime = (startTime: string): string => {
    try {
      const start = new Date(startTime);
      const now = new Date();
      const diffMs = now.getTime() - start.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);

      if (diffHours > 0) {
        return `${diffHours}h ${diffMins % 60}m`;
      } else {
        return `${diffMins}m`;
      }
    } catch {
      return 'Unknown';
    }
  };

  useEffect(() => {
    loadPipelines();

    // Auto-refresh every 10 seconds
    const interval = setInterval(loadPipelines, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Active Pipelines</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadPipelines} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && pipelines.length === 0 && (
        <Box display="flex" justifyContent="center" p={3}>
          <Typography>Loading active pipelines...</Typography>
        </Box>
      )}

      {!loading && pipelines.length === 0 && (
        <Alert severity="info">
          No active pipelines at the moment.
        </Alert>
      )}

      <Grid container spacing={2}>
        {pipelines.map((pipeline) => (
          <Grid item xs={12} key={pipeline.pipelineId}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box flex={1}>
                    <Typography variant="h6" gutterBottom>
                      {pipeline.purpose}
                    </Typography>
                    <Box display="flex" gap={1} mb={1}>
                      <Chip
                        label={`Step ${pipeline.currentStep}/6`}
                        size="small"
                        color="primary"
                      />
                      <Chip
                        label={pipeline.creator}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={pipeline.modelName}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        label={formatElapsedTime(pipeline.startTime)}
                        size="small"
                        color="default"
                      />
                    </Box>
                  </Box>
                  <Box display="flex" gap={1}>
                    {onWatchPipeline && (
                      <Tooltip title="Watch Pipeline">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => onWatchPipeline(pipeline.pipelineId)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Cancel Pipeline">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleCancelPipeline(pipeline.pipelineId)}
                        disabled={cancellingId === pipeline.pipelineId}
                      >
                        <StopIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box>
                  <Box display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {Math.round(pipeline.progress)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pipeline.progress}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Pipeline ID: {pipeline.pipelineId}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ActivePipelines;
